import { useCallback, useEffect, useState } from 'react';
import { isEqual, isNull } from 'lodash';
import { useSelector } from 'react-redux';
import { formatUserPercentageInput } from 'utils/formatters';
import { Store } from 'components/common/types/Store.types';
import { getLocaleCode } from 'store/publisher/selectors';
import { sotType, SotTypes, SotValues } from './ShareOfTime.types';

type UseSot = SotValues<number | null | undefined> & {
  getValues: () => Partial<SotValues<number>>;
  isValid: () => boolean;
  isToggled: boolean;
  setSot: (type: SotTypes, value: string) => void;
  handleToggle: (value: boolean) => void;
};

export const useSot = (): UseSot => {
  const ceilingSot = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.ceilingSot);
  const floorSot = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.floorSot);
  const sot = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.sot);
  const localeCode = useSelector(getLocaleCode);

  const [isToggled, setIsToggled] = useState(false);
  const [currentSotValues, setCurrentSotValues] = useState<SotValues<number | null | undefined>>({
    sot: null,
    floorSot: null,
    ceilingSot: null,
  });

  useEffect(() => {
    const newValues =
      floorSot || ceilingSot
        ? {
            sot: null,
            floorSot: floorSot ?? null,
            ceilingSot: ceilingSot ?? null,
          }
        : {
            sot: sot ?? null,
            floorSot: null,
            ceilingSot: null,
          };

    if (!isEqual(currentSotValues, newValues)) {
      setCurrentSotValues(newValues);
    }
  }, [sot, floorSot, ceilingSot]);

  useEffect(() => {
    setIsToggled(!!(floorSot || ceilingSot));
  }, [floorSot, ceilingSot]);

  const amendValueToConstraint = useCallback(
    (type: SotTypes, value: string): number => {
      const formattedUserInput = formatUserPercentageInput(value, localeCode);

      if (type === sotType.FLOOR && currentSotValues.ceilingSot && formattedUserInput > currentSotValues.ceilingSot) {
        return formatUserPercentageInput(currentSotValues.ceilingSot, localeCode);
      }
      if (type === sotType.CEILING && currentSotValues.floorSot && formattedUserInput < currentSotValues.floorSot) {
        return formatUserPercentageInput(currentSotValues.floorSot, localeCode);
      }
      return formattedUserInput;
    },
    [currentSotValues.floorSot, currentSotValues.ceilingSot],
  );

  const setSot = (type: SotTypes, value: string): void => {
    setCurrentSotValues((curr) => ({
      ...curr,
      [type]: amendValueToConstraint(type, value),
    }));
  };

  const handleToggle = (value: boolean): void => {
    setIsToggled(value);
    setCurrentSotValues({
      ceilingSot: undefined,
      floorSot: undefined,
      sot: null,
    });
  };

  const isValid = (): boolean => {
    const isDefinedAndNonZero = (value: number | null | undefined): boolean => value != null && value !== 0;

    const isRangeCleared = currentSotValues.floorSot === undefined && currentSotValues.ceilingSot === undefined;
    const isSotDefined = !isNull(currentSotValues.sot);
    const areCeilingAndFloorDefined = [currentSotValues.ceilingSot, currentSotValues.floorSot].every(
      isDefinedAndNonZero,
    );
    const isCeilingSotGreaterThanFloorSot = (currentSotValues.ceilingSot ?? 0) > (currentSotValues.floorSot ?? 0);

    return isRangeCleared || isSotDefined || (areCeilingAndFloorDefined && isCeilingSotGreaterThanFloorSot);
  };

  const getValues = (): Partial<SotValues<number>> => {
    return Object.values(sotType).reduce((acc, type: SotTypes) => {
      const value = currentSotValues[type];

      return {
        ...acc,
        [type]: value ? amendValueToConstraint(type, value.toString()) : undefined,
      };
    }, {});
  };

  return {
    ...currentSotValues,
    isToggled,
    setSot,
    handleToggle,
    isValid,
    getValues,
  };
};
